/* eslint-disable @typescript-eslint/no-explicit-any */

import { TLEditorSnapshot } from "tldraw";


import { ColorVariant } from "@/components/canvas-editor/components/Container";
import {
  AttributeCompareFilter,
  DateBetweenFilter,
  HourOfDayBetweenFilter,
  RatioCompareFilter,
  EmbedFilter,
  ContentModerationFilter,
  ExclusionMatchFilter,
  ListMemberFilter,
  AccountLabelFilter,
  PostLabelFilter,
  MatchFilter,
  SocialGraphFilter,
  SocialListFilter,
  StarterPackFilter,
  MagicAudienceFilter,
  ModelProbabilityFilter,
  TextSimilarityFilter,
  AnyMatchFilter,
  NoneMatchFilter,
  EntityListFilter,
  LanguageAnalysisFilter,
  SentimentAnalysisFilter,
  FinancialSentimentAnalysisFilter,
  ToxicityAnalysisFilter,
  EmotionAnalysisFilter,
  SpamAnalysisFilter,
  TopicAnalysisFilter,
  ComparisonOperator,
  TextCategorizationFilter,
  ImageModerationFilter,
  ImageCategorizationFilter,
  Pattern,
  PostTypeFilter,
  ParamCompareFilter,
  OpenAIFilter,
  EachFilter,
  LanguageAnalysisCategory,
} from "@/components/canvas-editor/shapes/shapes";
import { SimpleEditorContentType } from "@/components/SimpleEditor";
import { FeedAnalytics, StatsSummary } from "@/lib/api";
import type { LensSetting } from "@/lib/lens";

export type DependencyIntent = "pinned" | "compatible" | "latest";

export interface SimpleEditorSnapshot {
  hideNsfw?: boolean;
  type: "simple";
  exclusions: FilterTypes[];
  inclusions: FilterTypes[];
  language: LanguageAnalysisCategory | "any";
  contentType: SimpleEditorContentType;
}

export function isSimpleEditorSnapshot(
  snapshot: unknown,
): snapshot is SimpleEditorSnapshot {
  return (
    Boolean(snapshot) &&
    typeof snapshot === "object" &&
    snapshot !== null &&
    "type" in snapshot &&
    snapshot.type === "simple"
  );
}

export interface FeedVersion {
  is_legacy: boolean;
  metadata?: {
    snapshot?: TLEditorSnapshot | SimpleEditorSnapshot;
    testPosts?: string[];
  };
  manifest: Manifest;
  emit?: EmitOption;
  onEmitNull?: OnEmitNullOption;
  created_at?: string;
  /** undocumented feature for custom apps */
  content_mode?: string;
  order: SortingOption | undefined;
  custom_sort_settings?: SortingParams;
}

interface StatBlock {
  date: string;
  page_renders: number;
  post_renders: number;
  unique_users: number;
}

export interface NodeMetadata {
  title?: string;
  description?: string;
  color?: ColorVariant;
  pattern?: Pattern;
  component_id?: number;
  component_name?: string;
  version_id?: number;
  isCustomNode?: boolean;
  customNodeAutoUpdate?: boolean;
  /**
   * The dependency constraint an author declared for this embedded node:
   * "pinned" (this exact version), "compatible" (newest non-breaking, the
   * default for a new embed), or "latest" (newest, breaking or not).
   *
   * Absent means "pinned" everywhere — server and client — so no existing body
   * has to be rewritten, and no compiled bytes move. Leaf metadata survives
   * compilation into the condition, so those bytes are not free.
   */
  dependency_intent?: DependencyIntent;
  customNodeParameters?: FeedParameter[];
  customNodeParameterGroups?: {
    name: string;
    id: string;
    description?: string;
  }[];
  mustIncludeInSimpleFeed?: boolean;
}

interface FeedParameterBase {
  name: string;
  displayName?: string;
  description: string;
  group?: string;
}
export interface FeedInputParameter extends FeedParameterBase {
  exampleValue: string;
  value?: string;
  type?: "input" | "regex" | undefined;
}

export interface FeedListParameter extends FeedParameterBase {
  exampleValue: string[];
  value?: string[];
  type: "list";
}

export interface FeedToggleParameter extends FeedParameterBase {
  exampleValue: boolean;
  value?: boolean;
  type: "toggle";
}

export interface FeedNumberParameter extends FeedParameterBase {
  exampleValue: number;
  value?: number;
  isPercentage?: boolean;
  type: "number";
}

export interface FeedSelectParameter extends FeedParameterBase {
  exampleValue: string;
  value?: string;
  options: string[];
  labels?: string[];
  type: "select";
}

export type FeedParameter =
  | FeedInputParameter
  | FeedListParameter
  | FeedToggleParameter
  | FeedNumberParameter
  | FeedSelectParameter;

export function groupCustomNodeParameters(parameters: FeedParameter[]) {
  return parameters.reduce((acc, parameter, index) => {
    if (parameter.group) {
      acc[parameter.group] = [
        ...(acc[parameter.group] || []),
        { ...parameter, originalIndex: index },
      ];
    } else {
      acc["none"] = [
        ...(acc["none"] || []),
        { ...parameter, originalIndex: index },
      ];
    }

    return acc;
  }, {} as Record<string, (FeedParameter & { originalIndex: number })[]>);
}

export interface ManifestNodeMetadata {
  metadata?: NodeMetadata | null;
}

export interface Feed {
  monetization_available: boolean;
  contrails: boolean;
  sponsorship_saturation_rate: number | null;
  cpm_cents?: number;
  public: boolean;
  avatar_url: string;
  algorithm_uri: string;
  algorithm_bio?: string;
  algorithm_manifest_id: number | null;
  draft_algorithm_manifest_id: number | null;
  draft_algorithm_manifest: FeedVersion;
  record_name: string;
  display_name: string;
  description: string;
  feed_did: string;
  active: boolean;
  status?: "migrating" | "migrated" | "migration_error";
  unique_operator_ids: Array<number>;
  user_id: number;
  id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  stats: StatBlock;
  /**
   * Somebody else owns this feed and has made you a collaborator on it. Only ever true in a
   * listing that asked for shared feeds (`access`); absent elsewhere, which reads as false.
   */
  shared_with_me?: boolean;
  /** What you have been granted on it. Empty when you own it — an owner may do everything. */
  my_capabilities?: string[];
  subscriber_lists: Array<{
    id: number;
    display_name: string;
    list_type: "memberships" | "blocklists";
  }>;
  tags: SavedTag[];
  settings?: {
    show_nsfw?: boolean;
    /**
     * Hands this feed's ordering to the bandit. Follows the saved page: having a Gym Layer turns it
     * on, removing one turns it off (see canvas-editor/gym-settings.ts). Older feeds enrolled
     * without a layer (the whole-page gym) keep it until they remove a layer they never had.
     */
    feed_gym_enabled?: boolean;
    /**
     * Fraction of this feed's readers the gym may experiment on, 0-1.
     *
     * Absent means the fleet default (`gym_share.FEED_GYM_DEFAULT_SHARE`, 0.25 in production). A
     * feed with a Gym LAYER is pinned to 1 on save: the layer's share already bounds the exposure,
     * so a reader fraction under 1 only throws evidence away. Do not read absence as off;
     * `feed_gym_enabled` is what turns it off.
     */
    feed_gym_share?: number;
    /** Daily co/core-generated summary at the top of the public page. Owner opt-in. */
    feed_pulse_enabled?: boolean;
    editor_mode?: "json" | "canvas";
    // backend doesnt support real json
    test_posts?: string;
    members_only_post?: Omit<Post, "id">[];
    banned_post?: Omit<Post, "id">[];
  };
}

export interface Tag {
  display_name: string;
  color: ColorVariant;
}

export interface SavedTag extends Tag {
  id: number;
}

export type PublicFeed = Omit<Feed, "stats"> & {
  stats: {
    details: FeedAnalytics[];
    summary: StatsSummary;
  };
  is_video_feed: boolean;
};

export interface FeedHistory extends Feed {
  algorithm_manifests: FeedVersion[];
}

export interface Post {
  algorithm_uri: string;
  algorithm_manifests: FeedVersion[];
  algorithm_manifest_id: number | null;
  draft_algorithm_manifest_id: number | null;
  draft_algorithm_manifest: FeedVersion;
  record_name: string;
  display_name: string;
  description: string;
  feed_did: string;
  active: boolean;
  unique_operator_ids: Array<number>;
  user_id: number;
  id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  sticky_id?: number;
  author: PostAuthor;
  cid: string;
  indexedAt: string;
  record: PostRecord;
  uri: string;
  embed?: PostEmbed;
  labels: Array<{ val: "porn" | "sexual" | "graphic-media" | "nudity" }>; // Adjust type if you have label definitions
  likeCount: number;
  quoteCount: number;
  replyCount: number;
  repostCount: number;
  threadgate?: any; // Adjust type if threadgate details are known
  viewer: PostViewerState;
  $type: string;
  hidden: boolean;
}

export interface FeedPage {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  user_algos: Array<Feed>;
}

export interface PostHydrationResponse {
  posts: Post[];
  cursor: string;
}

export interface ExternalFeed {
  cid: string;
  creator: PostAuthor;
  did: string;
  display_name: string;
  indexed_at: string;
  avatar_url: string;
  uri: string;
  accepts_interactions: null;
  avatar: null;
  description: string;
  description_facets: null;
  labels: [];
  like_count: 0;
  viewer: {
    like: null;
    py_type: "app.bsky.feed.defs#generatorViewerState";
  };
  py_type: "app.bsky.feed.defs#generatorView";
}

interface PostAuthor {
  did: string;
  handle: string;
  associated?: PostAuthorAssociated;
  avatar?: string;
  createdAt: string;
  displayName?: string;
  labels: any[]; // Adjust type if you have label definitions
  viewer: PostAuthorViewerState;
  $type: string;
}

interface PostAuthorAssociated {
  chat?: {
    allowIncoming: string;
    $type: string;
  };
  feedgens?: any;
  labeler?: any;
  lists?: any;
  starterPacks?: any;
  $type: string;
}

interface PostAuthorViewerState {
  blockedBy: boolean;
  blocking?: string | null;
  blockingByList?: string | null;
  followedBy?: string | null;
  following?: string | null;
  knownFollowers?: any;
  muted: boolean;
  mutedByList?: string | null;
  $type: string;
}

interface PostRecord {
  createdAt?: string;
  text: string;
  embed?: PostEmbedExternal;
  entities?: any;
  facets?: any;
  labels?: any;
  langs: string[];
  reply?: any;
  tags?: any;
  $type: string;
}

interface PostEmbed {
  external?: PostEmbedExternalView;
  $type: string;
}

interface PostEmbedExternalView {
  description: string;
  title: string;
  uri: string;
  thumb?: string; // URL of the thumbnail image
  $type: string;
}

interface PostEmbedExternal {
  external?: PostEmbedExternalViewBlob;
  alt?: string;
  $type: string;
}

interface PostEmbedExternalViewBlob {
  description: string;
  title: string;
  uri: string;
  thumb: {
    mimeType: string;
    size: number;
    ref: {
      $link: string;
    };
    $type: string;
  };
  $type: string;
}

interface PostViewerState {
  embeddingDisabled: boolean;
  like?: any;
  replyDisabled?: any;
  repost?: any;
  threadMuted: boolean;
  $type: string;
}

export interface UserHydrationResponse {
  users: User[];
}


export type FeatureFlag =
  // 🔑 INERT since 2026-10-02: the Feed Gym is generally available and nothing reads this. Kept,
  // like `lenses` below, so the 23 rows that still hold it validate rather than failing to parse.
  | "feed_gym"
  // `lenses` and `lens_groups` are INERT since 2026-09-21: layers, audiences and audience
  // groups are generally available and nothing reads either flag. Kept so the rows that
  // still hold them validate.
  | "lenses"
  | "lens_groups"
  | "migrate"
  | "monetization"
  | "new_orders"
  // The operator this gates was renamed to `text_predicate`; the flag was not.
  // Both are accepted so a grant of either works, and `text_predicate` is the
  // one to use from now on — a flag named after an operator that no longer
  // exists is how this came to be granted to nobody at all.
  | "openai_prompt"
  | "text_predicate"
  // "Ask a Question" (the `text_question` operator). One name only -- unlike the pair above,
  // this operator has never been renamed, so there is no synonym to accept and no way to
  // repeat the mismatch that left Natural Language granted to nobody.
  | "text_question"
  | "personalization"
  | "programmatic-ads"
  // Feed Pulse beta: shows the daily co/core summary toggle and the co/core connection block.
  | "feed_pulse";

export interface User {
  id: number;
  did: string;
  user_id: string;
  handle: string;
  username: string;
  display_name: string;
  description: string | null;
  avatar: string;
  banner: string | null;
  followers_count: number;
  follows_count: number;
  posts_count: number;
  associated_feedgen_count: number;
  associated_list_count: number;
  associated_starter_packs_count: number;
  associated_magic_audiences_count: number;
  associated_model_probabilities_count: number;
  pinned_post_cid: string | null;
  pinned_post_uri: string | null;
  bluesky_created_at: string;
  bluesky_indexed_at: string;
  indexed_at: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  feature_flags?: FeatureFlag[];
  account?: {
    bio?: string;
    email?: string;
    license_acceptances?: Record<string, string>;
  };
  settings?: {
    show_nsfw?: boolean;
    sidebar_feeds?: string;
  };
  is_programmatic_partner?: boolean;
  programmatic_partner?: {
    active: boolean;
    approved: boolean;
    has_api_key: boolean;
    id: number;
    partner_name: string;
    partner_type: "dsp";
  };
}

interface AndStatement extends ManifestNodeMetadata {
  and: FilterTypes[];
}

interface OrStatement extends ManifestNodeMetadata {
  or: FilterTypes[];
}

export interface Manifest {
  filter: FilterTypes;
}

export type ThresholdLeafValue = {
  op: ComparisonOperator;
  value: number;
};

type ThresholdMatchFilter<T extends string = string> = {
  [key in T]: ThresholdLeafValue;
};

type ThresholdLeaf =
  | ThresholdMatchFilter<"like_count">
  | ThresholdMatchFilter<"reply_count">
  | ThresholdMatchFilter<"repost_count">
  | ThresholdMatchFilter<"reader_like_count">
  | ThresholdMatchFilter<"reader_reply_count">
  | ThresholdMatchFilter<"reader_repost_count">;

/**
 * Posts by these authors pass regardless of the counts. Put it in an OR beside a count
 * gate — "a trusted outlet goes straight in, anyone else needs traction". DIDs only; the
 * server rejects anything else rather than guess at a handle.
 */
export type ThresholdAuthorLeaf = {
  author_in: { op?: "in" | "not_in"; value: string[] };
};

export type ThresholdFilters = ThresholdLeaf | ThresholdAuthorLeaf | Thresholds;

export type Thresholds = Partial<Record<"AND" | "OR", ThresholdFilters[]>>;

export interface SortingParams {
  /**
   * Several audiences on one feed, each taking a share of the page.
   *
   * Supersedes `lens` rather than sitting beside it: the serve path reads `lenses` FIRST and never
   * looks at `lens` when it is present, so a feed carries one or the other. Typed loosely for the
   * same reason `lens` is — see its comment below, and `readableLensList` is the gate.
   */
  lenses?: unknown;
  like_count_multiplier?: number;
  reply_count_multiplier?: number;
  repost_count_multiplier?: number;
  reader_like_count_multiplier?: number;
  reader_reply_count_multiplier?: number;
  reader_repost_count_multiplier?: number;
  request_less_count_multiplier?: number;
  request_more_count_multiplier?: number;
  decay_penalty?: number;
  time_window?: number;
  thresholds?: Thresholds;
  gravity?: number;
  time_offset?: number;
  /** Lookback window for the trending/scoring queries, in days. Read by feed-processor, carried by the ui. */
  max_days?: number;
  /**
   * Viewer-graph lens: filter the served page to authors in the reader's own
   * graph ("this feed, but only people I follow").
   *
   * Deliberately a feed *setting* rather than a filter node. A lens is applied
   * at serve time against the reader, not at match time against the post, so it
   * cannot compose with `and`/`or` alongside match-time conditions — and a new
   * operator inside `manifest.filter` would be rejected by the matching engines,
   * silently taking the whole feed dark. Living here keeps the firehose matchers
   * entirely viewer-unaware; they already ignore unknown `custom_sort_settings`
   * keys today.
   */
  lens?: LensSetting;
  // Wilson algorithm parameters
  wilson_confidence_z?: number;
  wilson_interaction_like_multiplier?: number;
  wilson_interaction_reply_multiplier?: number;
  wilson_interaction_repost_multiplier?: number;
  wilson_interaction_quote_multiplier?: number;
  wilson_request_more_multiplier?: number;
  wilson_request_less_multiplier?: number;
  // For You algorithm parameters
  foryou_similarity_threshold?: number;
  foryou_similar_users_limit?: number;
  foryou_recent_posts_limit?: number;
  foryou_half_life_hours?: number;
  foryou_smoothing_factor?: number;
  foryou_popularity_penalty?: number;
  foryou_divisor_power?: number;
  foryou_time_shift_hours?: number;
  foryou_ignore_order?: boolean;
}

type LeafFilterTypes =
  | MatchFilter
  | ExclusionMatchFilter
  | PostTypeFilter
  | AnyMatchFilter
  | NoneMatchFilter
  | AttributeCompareFilter
  | RatioCompareFilter
  | HourOfDayBetweenFilter
  | DateBetweenFilter
  | EmbedFilter
  | TextSimilarityFilter
  | SocialGraphFilter
  | StarterPackFilter
  | ListMemberFilter
  | AccountLabelFilter
  | PostLabelFilter
  | MagicAudienceFilter
  | SocialListFilter
  | ModelProbabilityFilter
  | ContentModerationFilter
  | EntityListFilter
  | LanguageAnalysisFilter
  | SentimentAnalysisFilter
  | FinancialSentimentAnalysisFilter
  | ToxicityAnalysisFilter
  | EmotionAnalysisFilter
  | SpamAnalysisFilter
  | TopicAnalysisFilter
  | TextCategorizationFilter
  | ImageModerationFilter
  | ImageCategorizationFilter
  | ParamCompareFilter
  | OpenAIFilter;
/**
 * A custom node as a REFERENCE rather than an expansion.
 *
 * The editor used to substitute parameters into a node's template itself and
 * post the result, which meant substitution existed twice — here and in Python
 * — and the two drifted. Now the editor posts what the node IS and the server
 * compiles it, so there is one implementation and the bytes a user previews are
 * the bytes that get stored.
 *
 * The server normalizes these away before any writer sees the manifest (see
 * app/components_v2/reference_leaves.py), so this format is safe to send
 * regardless of whether a feed has been cut over.
 */
interface ComponentReferenceFilter {
  $component: {
    component_id: number;
    /** Omitted only when track_latest is true and any version will do. */
    version?: number;
    /** Only the parameters the user actually set, keyed by name. */
    values: Record<string, unknown>;
    track_latest?: boolean;
    /**
     * The declared dependency constraint. Reference leaves are a wire format,
     * so a field the editor omits is a field the save can never recover.
     */
    dependency_intent?: DependencyIntent;
    /** Stable identity for this instance within the feed, when known. */
    instance_id?: string;
    /** Cosmetic fields the server copies onto the stored metadata. */
    display?: {
      title?: string;
      description?: string;
      color?: string;
      component_name?: string;
    };
    customNodeParameterGroups?: unknown;
  };
  metadata?: NodeMetadata;
}

export type FilterTypes =
  | LeafFilterTypes
  | AndStatement
  | OrStatement
  | EachFilter;

/**
 * A filter tree on its way to the server, where custom nodes have been replaced
 * by references.
 *
 * Deliberately NOT part of `FilterTypes`: this shape only ever exists between
 * serializing a save and the server normalizing it back. Widening `FilterTypes`
 * instead would ripple into every route loader whose type the router generates
 * from it, for a shape the editor never actually holds.
 */
export type SaveFilter =
  | ComponentReferenceFilter
  | LeafFilterTypes
  | { and: SaveFilter[]; metadata?: NodeMetadata | null }
  | { or: SaveFilter[]; metadata?: NodeMetadata | null }
  | { each: [string, SaveFilter]; metadata?: NodeMetadata | null };

export interface SaveManifest extends Omit<Manifest, "filter"> {
  filter: SaveFilter;
}
export type SortingOption =
  | "new"
  | "blend"
  | "trending"
  | "old"
  | "hackernews"
  | "wilson"
  | "personalized";

export const emitOptions = ["parent", "root", "self", "quoted-post"] as const;
export type EmitOption = (typeof emitOptions)[number];

export const onEmitNullOptions = ["emit-self", "skip"] as const;
export type OnEmitNullOption = (typeof onEmitNullOptions)[number];

/**
 * What the feed will actually DO with a post, which is not the same question as whether
 * the post matches.
 *
 * Debug reports pass/fail on the post in front of it. On a feed whose `emit` is
 * `parent`/`quoted`/`root` that verdict does not decide feed membership in either
 * direction: a match is delivered as the related post (or discarded when there is none,
 * since `onEmitNull` defaults to skipping), and a non-match can be in the feed because
 * some other post matched and named this one. The server sends this only when `emit`
 * actually redirects, so on an ordinary feed the map stays empty.
 *
 * `notable` is the server saying it has something to add beyond the pass/fail already on
 * screen. Rendering every status regardless would put a paragraph under every post on the
 * ~7,649 of 7,666 feeds that emit normally.
 */
export interface DeliveryStatus {
  uri: string;
  emit: string;
  on_emit_null: string | null;
  /** The post the feed will show instead, when `outcome` is `redirected`. */
  target_uri: string | null;
  outcome:
    | "self"
    | "redirected"
    | "dropped"
    | "not_matched"
    | "not_matched_but_present";
  notable: boolean;
  explanation: string | null;
}

export interface StickyPost {
  id: number;
  /**
   * The association row's own id. `id` above is the sticky_post id, which is NOT
   * unique per feed: the same post can be stuck to one feed as both `pinned` and
   * `injected`, and each is a separate row. Send this back when editing or
   * deleting so the server touches the copy the reader actually picked.
   */
  assoc_id?: number;
  is_active: boolean;
  sticky_type: string;
  hidden: boolean;
  uri: string;
  post: Post;
  author: string;
  created_at: string;
  updated_at: string;
  order?: number;
  stopping_criteria?: {
    expiration_time?: number;
  };
}

export interface TopContentResponse {
  url: string;
  action_identifier: string;
  clickthrough_author_count: number;
  clickthrough_embed_count: number;
  clickthrough_item_count: number;
  clickthrough_reposter_count: number;
  engagement_rate: number;
  interaction_like_count: number;
  interaction_quote_count: number;
  interaction_reply_count: number;
  interaction_repost_count: number;
  interaction_seen_count: number;
  like_count: number;
  reader_engagement: number;
  reader_like_count: number;
  reader_reply_count: number;
  reader_repost_count: number;
  reply_count: number;
  repost_count: number;
  request_less_count: number;
  request_more_count: number;
  total_interactions: number;
  post: Post;
}

export interface StarterPackRecord {
  cid: string;
  creator: User;
  indexed_at: string;
  record: {
    created_at: string;
    list: string;
    name: string;
    description: string;
    description_facets: null;
    updatedAt: "2024-11-24T18:22:19.694Z";
  };
  uri: string;
  joined_all_time_count: 0;
  joined_week_count: 0;
  labels: [];
  list: null | {
    cid: string;
    name: string;
    purpose: "app.bsky.graph.defs#referencelist";
    uri: string;
    avatar: string | undefined;
    indexed_at: string;
    labels: [];
    list_item_count: 124;
  };
  list_items_sample: Array<{
    subject: User;
    uri: string;
    py_type: "app.bsky.graph.defs#listItemView";
  }>;
  py_type: "app.bsky.graph.defs#starterPackView";
}

export interface ListRecord {
  cid: string;
  creator: User;
  indexed_at: string;
  created_at: string;
  list: undefined;
  name: string;
  description: string;
  description_facets: null;
  avatar: string | undefined;
  updatedAt: "2024-11-24T18:22:19.694Z";
}

interface CampaignPost {
  author: string;
  created_at: string;
  deleted_at: string | null;
  details: {
    is_active: boolean;
    all_time_total_views: number;
    all_time_unique_user_views: number;
    today_total_views: number;
    today_unique_user_views: number;
  };
  id: number;
  updated_at: string;
  uri: string;
}

export const campaignStateOptions = [
  "proposed",
  "denied",
  "accepted",
  "active",
  "inactive",
  "deactivated",
  "rescinded",
  "cancelled",
  "completed",
] as const;
export type CampaignState = (typeof campaignStateOptions)[number];

export interface CampaignStatus {
  unread_message_count: number;
  state: CampaignState;
  hidden: boolean;
  cpm_cents: number;
  cpm_inflight: number;
  impressions_budget: number;
  impressions_to_date: number;
  days_to_completion: number;
  stopping_criteria?: {
    reason?: string;
  };
  metadata?: {
    reason?: string;
  };
}

export interface Campaign {
  id: number;
  display_name: string;
  description: string;
  created_at: string;
  deleted_at: string | null;
  updated_at: string;
  user: User;
  user_id: number;
  sticky_posts: Array<CampaignPost>;
  unread_messages_count: number;
  algorithms: Array<Feed & { details: CampaignStatus }>;
}

export interface InboundCampaign extends Campaign {
  campaign_status: CampaignStatus & {
    algorithm?: Feed;
  };
}

/** @public Work in progress: written ahead of its consumer. */
export interface ProgrammaticAssetCampaign {
  id: number;
  display_name: string;
  cpm_inflight: number;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface ProgrammaticAsset {
  id: number;
  uri: string;
  text: string;
  link?: string | null;
  author: string;
  created_at: string;
  campaigns: ProgrammaticAssetCampaign[];
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface ProgrammaticAssetsPagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

export interface ProgrammaticAssetsResponse {
  assets: ProgrammaticAsset[];
  pagination: ProgrammaticAssetsPagination;
}

/** One claim in a pulse, resolved to the post it came from. The model only ever
 *  emits an index; the API maps it onto the posts it was given, so `post_url`
 *  is either a real link or absent. */
export interface PulseCitation {
  index: number;
  text: string;
  post_uri: string | null;
  post_url: string | null;
}

/** A day's Feed Pulse: the co/core-generated summary served on the public feed page. */
export interface FeedPulse {
  algorithm_id: number;
  pulse_date: string | null;
  generated_at: string | null;
  headline: string | null;
  /** Pre-2026-09-12 pulses: one prose paragraph with unlinkable [n] markers. */
  summary: string | null;
  /** The day's most striking line, copied verbatim out of one post.
   *  Optional: a payload cached before 2026-09-12 carries none of these. */
  quote?: PulseCitation | null;
  /** One sentence per post, most important first. Each links to its post. */
  beats?: PulseCitation[];
  /** The disagreement or open question someone raised, when there was one. */
  tension?: PulseCitation | null;
  themes: string[];
  standout: { post_uri: string | null; post_url?: string | null; why: string } | null;
  model: string;
  provider: {
    handle: string | null;
    displayName: string | null;
    machineLabel: string | null;
  } | null;
  credit_line: string | null;
  receipt_uri: string | null;
  receipt_url: string | null;
  verify_url: string | null;
  source_post_count: number;
  /** Where the posts came from. `top_viewed` is what readers actually looked at
   *  that day; `day` is the fallback when views were thin — the posts the feed
   *  MATCHED that day. `latest` is retired (newest ever, ignoring the day) but
   *  historical rows still carry it. */
  source_kind: "top_viewed" | "day" | "latest";
}

/** The owner's co/core connection as reported by GET /api/v1/cocore/status. */
export interface CocoreStatus {
  connected: boolean;
  status: "active" | "needs_reauth" | "revoked" | "out_of_credits" | null;
  needs_reauth: boolean;
  /** The owner's co/core balance is under the exchange's admission floor, so
   *  pulses are parked until credits return (weekly refresh, or earnings from
   *  serving). Nothing is revoked. */
  out_of_credits: boolean;
  /** Last co/core credit balance Graze read for this key, in CC. */
  balance: number | null;
  balance_checked_at: string | null;
  /** co/core's admission floor: a job is refused below this balance. */
  min_credits: number;
  /** Roughly what one pulse costs, in CC. */
  approx_pulse_cost: number;
  topup_url: string;
  key_prefix: string | null;
  source: "minted" | "paired" | "pasted" | null;
  /** One-click minting needs a granular rpc grant AIP cannot issue yet, so it
   *  is gated server-side. False means pairing is the path to offer. */
  can_mint: boolean;
  pairing: CocorePairing | null;
  connected_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  handle: string | null;
  signin_url: string;
  keys_url?: string;
}

export interface CocorePairing {
  user_code: string;
  verification_uri: string;
  expires_at: string;
  started_at: string;
}

export type CocorePairPoll =
  | ({ state: "pending" | "unavailable" } & CocorePairing)
  | { state: "none" | "denied" | "expired" }
  | { state: "did_mismatch"; approved_handle: string | null; message: string }
  | { state: "connected"; status: CocoreStatus };

/** Reply to POST /cocore/feeds/{id}/pulse-now. */
export interface FeedPulseNowResult {
  algorithm_id: number;
  pulse_date: string;
  state: "queued" | "running" | "done" | "failed" | "skipped";
  /** true when this call put a job in the queue (new, or a failed one retried). */
  queued: boolean;
}

export interface FeedPulseStatus {
  algorithm_id: number;
  enabled: boolean;
  public: boolean;
  owner_connected: boolean;
  owner_needs_reauth: boolean;
  owner_out_of_credits: boolean;
  owner_balance: number | null;
  min_credits: number;
  latest_pulse: FeedPulse | null;
  last_job: {
    pulse_date: string;
    state: "queued" | "running" | "done" | "failed" | "skipped";
    attempts: number;
    error_code: string | null;
    last_error: string | null;
    model: string | null;
    next_attempt_at: string | null;
    finished_at: string | null;
  } | null;
}

/**
 * One hour of a feed's spend on a rate-limited node.
 *
 * `limit` is the ceiling that was in force when the hour happened, not the current one:
 * a limit raised on Wednesday must not silently rewrite Tuesday's reading.
 */
export type RateLimitWindow = {
  window_start: number;
  window_secs: number;
  window_end: number;
  limit: number;
  /** Posts actually judged by the node. */
  scored: number;
  /** Posts that arrived after the budget was gone and were never judged. */
  refused: number;
  throttled: boolean;
  utilisation: number;
  /** Refused posts by the scope that refused them: feed, account or rule. */
  refused_by: Record<string, number>;
  first_refusal_at: number | null;
  last_refusal_at: number | null;
  rules: { rule_id: string; scored: number; refused: number }[];
};

/** A rate-limited condition as it appears in the feed's manifest. */
export type RateLimitedNode = {
  /**
   * Null for `text_question`. The matcher keys those on a `q_…` hash of a serialized spec
   * that the API deliberately does not re-derive — a near-miss would attribute one node's
   * usage to another silently, so per-node counts are omitted rather than guessed.
   */
  rule_id: string | null;
  operator: string;
  /** Which budget this node spends. */
  resource: string;
  /** `noul` | `choice` | `score` for a question; null for a predicate. */
  kind: string | null;
  /** The chosen option for a Choice, or "N levels" for a Score. */
  detail: string | null;
  predicate: string;
  comparator: string | null;
  threshold: number | null;
  /** What the author chose for posts they ran out of budget for. */
  over_quota: "exclude" | "include";
  /** Null where the id cannot be matched — see `rule_id`. */
  scored?: number | null;
  refused?: number | null;
};

type RateLimitGuidance = {
  code: string;
  severity: "info" | "warning";
  message: string;
  detail?: string;
};

export type FeedRateLimits =
  | {
      /**
       * False when the history cannot be read at all — a deployment with no sink, or one
       * that is down. Deliberately not the same as "you used nothing": rendering an
       * unreadable feed as a flat zero is the failure this whole page exists to end.
       */
      available: false;
      reason: "no_history_sink" | "history_unreadable";
      nodes: RateLimitedNode[];
      retention_days: number;
    }
  | {
      available: true;
      feed: { id: number; name: string | null };
      resource: string;
      limit: {
        units: number;
        window_secs: number;
        /**
         * Where this feed's ceiling comes from. `grant` is an authorized carve-out recorded
         * with a reason and a name; `override` is the break-glass someone set from inside the
         * cluster, which beats a grant deliberately.
         */
        source: "default" | "grant" | "override" | "account";
        account_units?: number;
        account_window_secs?: number;
      };
      current: {
        window_start: number;
        window_end: number;
        scored: number;
        refused: number;
        limit: number;
        utilisation: number;
        status: "ok" | "near" | "throttled";
        stale_after_seconds: number;
      };
      history: RateLimitWindow[];
      summary: {
        windows: number;
        throttled_windows: number;
        posts_scored: number;
        posts_unscored: number;
        peak_window_scored: number;
        first_throttled_at: number | null;
        last_throttled_at: number | null;
      };
      /** One block per metered budget. They do NOT share a unit — see `unit`. */
      resources: RateLimitResource[];
      nodes: RateLimitedNode[];
      hours: number;
      retention_days: number;
      /** Whether this viewer may authorize a carve-out. Decided by the server, never here. */
      can_grant: boolean;
      grants: QuotaGrant[];
      /** This author's own pending ask for a bigger budget, if they have one. */
      increase_request: QuotaIncreaseRequest | null;
    };

/** An author asking for a bigger budget. Changes nothing until an admin acts on it. */
export type QuotaIncreaseRequest = {
  id: number;
  feed: { id: number; name: string | null };
  resource: string;
  requested_units: number | null;
  reason: string;
  observed: {
    peak_window_scored: number | null;
    throttled_windows: number | null;
  };
  requested_by: { user_id: number | null; handle: string | null };
  outcome: string | null;
  resolution_note: string | null;
  resolved_by_user_id: number | null;
  resolved_at: string | null;
  granted_quota_grant_id: number | null;
  created_at: string | null;
  status: "open" | "granted" | "declined" | "withdrawn";
};

/** One metered budget for a feed: what it is, what it costs, and what it has spent. */
export type RateLimitResource = {
  /** `adjudication` | `text_question` */
  resource: string;
  /** What an author calls the node that spends it — "Natural Language", "Ask a Question". */
  label: string;
  /**
   * What ONE unit buys, in the plural. Not cosmetic: `adjudication` charges per post
   * judged and `text_question` per (question × post) pair, so the same number means
   * different things and the page must never say "posts" for both.
   */
  unit: string;
  unit_short: string;
  limit: {
    units: number;
    window_secs: number;
    source: "default" | "grant" | "override" | "account";
    account_units?: number;
    account_window_secs?: number;
  };
  current: {
    window_start: number;
    window_end: number;
    scored: number;
    refused: number;
    limit: number;
    utilisation: number;
    status: "ok" | "near" | "throttled";
    stale_after_seconds: number;
  };
  history: RateLimitWindow[];
  summary: {
    windows: number;
    throttled_windows: number;
    posts_scored: number;
    posts_unscored: number;
    peak_window_scored: number;
    first_throttled_at: number | null;
    last_throttled_at: number | null;
  };
  nodes: RateLimitedNode[];
  guidance: RateLimitGuidance[];
  /**
   * The carve-outs and the open request for THIS budget only.
   *
   * Both exist at the top level too, where they are every resource's mixed together — the
   * flat mirror kept for older clients. Read these instead: a grant raises one budget, and
   * showing a question carve-out under the Natural Language heading tells an author their
   * limit was raised when the budget they are exhausting was untouched.
   */
  grants: QuotaGrant[];
  increase_request: QuotaIncreaseRequest | null;
};

/** An authorized carve-out from the default budget. */
export type QuotaGrant = {
  id: number;
  algorithm_id: number;
  algorithm_name: string | null;
  resource: string;
  units: number;
  window_secs: number;
  reason: string;
  granted_by_user_id: number | null;
  created_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by_user_id: number | null;
  active: boolean;
};

export type RecentRateLimits = {
  available: boolean;
  reason?: string;
  days?: number;
  feeds: {
    id: number;
    name: string | null;
    throttled_windows: number;
    posts_unscored: number;
    limit: number;
    first_throttled_at: number | null;
    last_throttled_at: number | null;
  }[];
};
